<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Mesh gradient BG orbs -->
      <div class="orb-layer" aria-hidden="true">
        <div class="orb o1"></div>
        <div class="orb o2"></div>
        <div class="orb o3"></div>
        <div class="orb o4"></div>
        <div class="orb o5"></div>
      </div>

      <!-- Frosted nav bar -->
      <nav class="glass-nav">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="gn-logo" />
        <div class="gn-pair">
          <div class="gn-dot lo-dot"></div>
          <span class="gn-name">{{ loName }}</span>
          <span class="gn-x">+</span>
          <span class="gn-re">{{ realtorName }}</span>
        </div>

      </nav>

      <!-- Hero floating glass panel -->
      <section class="hero-section">
        <div class="hero-glass-panel">
          <div class="hgp-shine"></div>
          <div class="hgp-content">
            <div class="hgp-pill">
              <span class="pill-glow"></span>
              <span>Mortgage Partnership</span>
            </div>
            <h1 class="glass-h1" v-html="heroH1Html"></h1>
            <p class="glass-p">{{ heroBody }}</p>
            <div class="glass-btns">
              <a class="gb-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="gb-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
          </div>
        </div>

        <!-- Floating contact chips -->
        <div class="contact-orbit">
          <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="orbit-chip">
            <span class="chip-glyph">☎</span>{{ loinfo.phone_number }}
          </a>
          <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="orbit-chip">
            <span class="chip-glyph">✉</span>Email
          </a>
          <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="orbit-chip" target="_blank" rel="noreferrer">
            <span class="chip-glyph">⬡</span>Partner Site
          </a>
        </div>
      </section>

      <!-- Agent cards — floating frosted glass -->
      <section class="cards-section">

        <div class="agent-glass lo-glass">
          <div class="ag-prism lo-prism"></div>
          <div class="ag-shine"></div>
          <div class="ag-top">
            <div class="ag-badge lo-badge">
              <span>Loan Officer</span>
            </div>
            <div class="ag-avatar-wrap lo-wrap">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="ag-avatar" />
              <div class="ag-avatar-ring lo-ring"></div>
            </div>
          </div>
          <div class="ag-name">{{ loName }}</div>
          <div class="ag-role">{{ loTitle }}</div>
          <div v-if="loinfo && loinfo.nmls" class="ag-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
          <div class="ag-sep lo-sep"></div>
          <div class="ag-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="ag-link lo-alink">{{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="ag-link lo-alink">{{ loinfo.email }}</a>
          </div>
      
        </div>

        <div class="agent-glass re-glass">
          <div class="ag-prism re-prism"></div>
          <div class="ag-shine"></div>
          <div class="ag-top">
            <div class="ag-badge re-badge">
              <span>Partner</span>
            </div>
            <div class="ag-avatar-wrap re-wrap">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="ag-avatar" />
              <div class="ag-avatar-ring re-ring"></div>
            </div>
          </div>
          <div class="ag-name">{{ realtorName }}</div>
          <div class="ag-role">{{ realtor && realtor.brokerage_name }}</div>
          <div class="ag-sep re-sep"></div>
          <div class="ag-contacts">
            <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="ag-link re-alink">{{ realtor.phone_number }}</a>
            <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="ag-link re-alink">{{ realtor.email }}</a>
            <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="ag-link re-alink" target="_blank" rel="noreferrer">Website →</a>
          </div>
        </div>

      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandGlass",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Crystal clear path<br/><em>to your home.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Zodiak:ital,wght@0,400;0,700;1,400&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Zodiak:ital,wght@0,400;0,700;1,400&display=swap');

.page {
  min-height: 100vh;
  background: #080e1c;
  color: #e8eeff;
  font-family: 'Outfit', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --blue: #4a8fff;
  --violet: #a855f7;
  --teal: #22d3ee;
  --glass-bg: rgba(255,255,255,0.06);
  --glass-border: rgba(255,255,255,0.12);
  --glass-shine: rgba(255,255,255,0.08);
  --muted: #7888b0;
  overflow: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* ORB LAYER */
.orb-layer { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.orb { position: absolute; border-radius: 50%; filter: blur(80px); }
.o1 { width: 600px; height: 600px; background: radial-gradient(circle, rgba(74,143,255,0.35), transparent 70%); top: -150px; left: -100px; animation: orb-drift 14s ease-in-out infinite alternate; }
.o2 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(168,85,247,0.3), transparent 70%); top: 10%; right: -100px; animation: orb-drift 18s ease-in-out infinite alternate-reverse; }
.o3 { width: 400px; height: 400px; background: radial-gradient(circle, rgba(34,211,238,0.2), transparent 70%); bottom: 10%; left: 20%; animation: orb-drift 22s ease-in-out infinite alternate; }
.o4 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(198,156,57,0.2), transparent 70%); bottom: 5%; right: 15%; animation: orb-drift 16s ease-in-out infinite alternate-reverse; }
.o5 { width: 200px; height: 200px; background: radial-gradient(circle, rgba(74,143,255,0.2), transparent 70%); top: 50%; left: 50%; animation: orb-drift 20s ease-in-out infinite alternate; }
@keyframes orb-drift { from { transform: translate(0, 0) scale(1); } to { transform: translate(40px, -30px) scale(1.1); } }

/* NAV */
.glass-nav {
  position: relative; z-index: 10;
  display: flex; align-items: center; justify-content: space-between; padding: 18px 52px;
  background: var(--glass-bg);
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid var(--glass-border);
}
.gn-logo { width: 140px; max-height: 72px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.8; }
.gn-pair { display: flex; align-items: center; gap: 10px; font-size: 12px; }
.gn-dot { width: 7px; height: 7px; border-radius: 50%; animation: pulse 2.5s infinite; }
.lo-dot { background: var(--teal); box-shadow: 0 0 8px var(--teal); }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
.gn-name { color: rgba(232,238,255,0.85); font-weight: 500; }
.gn-x { color: var(--a); font-weight: 300; }
.gn-re { color: var(--muted); }
.gn-cta {
  font-size: 12px; font-weight: 600; text-decoration: none;
  background: linear-gradient(135deg, var(--blue), var(--violet));
  color: #fff; padding: 10px 22px; border-radius: 8px;
  box-shadow: 0 4px 20px rgba(74,143,255,0.35);
  transition: box-shadow 0.2s, transform 0.18s;
}
.gn-cta:hover { box-shadow: 0 8px 30px rgba(74,143,255,0.5); transform: translateY(-1px); }

/* HERO */
.hero-section { margin:auto;position: relative; z-index: 1; padding: 60px 52px 40px; display: flex; flex-direction: column; align-items: flex-start; max-width: 1200px; margin: 0 auto; width: 100%; }
.hero-glass-panel {
  background: var(--glass-bg);
  backdrop-filter: blur(24px) saturate(200%);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  padding: 48px;
  max-width: 680px;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1);
  margin:auto
}
.hgp-shine {
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
}
.hgp-content { position: relative; z-index: 1; }
.hgp-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14); padding: 6px 14px; border-radius: 999px; font-size: 11px; color: rgba(232,238,255,0.7); margin-bottom: 22px; }
.pill-glow { width: 6px; height: 6px; border-radius: 50%; background: var(--teal); box-shadow: 0 0 8px var(--teal); }
.glass-h1 { font-family: 'Outfit', sans-serif; font-size: clamp(36px, 5vw, 62px); font-weight: 700; line-height: 1.06; color: #f0f4ff; margin: 0 0 18px; letter-spacing: -0.02em; }
.glass-h1 em { font-style: italic; background: linear-gradient(135deg, var(--teal), var(--blue)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; font-weight: 400; }
.glass-p { font-size: 15px; line-height: 1.78; color: var(--muted); margin-bottom: 32px; font-weight: 300; }
.glass-btns { display: flex; gap: 10px; flex-wrap: wrap;justify-content: center; }
.gb-primary { font-size: 13px; font-weight: 600; text-decoration: none; background: linear-gradient(135deg, var(--blue), var(--violet)); color: #fff; padding: 13px 28px; border-radius: 10px; box-shadow: 0 4px 20px rgba(74,143,255,0.35); transition: box-shadow 0.2s; width: 55%; text-align: center; }
.gb-primary:hover { box-shadow: 0 8px 32px rgba(74,143,255,0.5); }
.gb-ghost { font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); background: var(--glass-bg); border: 1px solid var(--glass-border); padding: 13px 22px; border-radius: 10px; transition: background 0.18s; backdrop-filter: blur(8px); }
.gb-ghost:hover { background: rgba(255,255,255,0.1); }

/* CONTACT CHIPS */
.contact-orbit { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 20px; }
.orbit-chip {
  display: flex; align-items: center; gap: 7px; font-size: 11px; text-decoration: none;
  color: rgba(232,238,255,0.6); background: var(--glass-bg);
  backdrop-filter: blur(12px); border: 1px solid var(--glass-border);
  padding: 7px 14px; border-radius: 999px;
  transition: border-color 0.18s, color 0.18s;
}
.orbit-chip:hover { border-color: var(--teal); color: var(--teal); }
.chip-glyph { font-size: 10px; opacity: 0.7; }

/* AGENT CARDS */
.cards-section { position: relative; z-index: 1; display: flex; gap: 20px; padding: 20px 52px 60px; max-width: 1200px; margin: 0 auto; width: 100%; flex-wrap: wrap; }
.agent-glass {
  flex: 1; min-width: 280px;
  background: var(--glass-bg);
  backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 24px;
  position: relative; overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}
.agent-glass:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.3); }
.ag-prism { position: absolute; top: 0; left: 0; right: 0; height: 2px; }
.lo-prism { background: linear-gradient(90deg, var(--blue), var(--teal), var(--violet)); }
.re-prism { background: linear-gradient(90deg, var(--a), var(--violet), var(--blue)); }
.ag-shine { position: absolute; top: 2px; left: 12px; right: 12px; height: 1px; background: rgba(255,255,255,0.15); }
.ag-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.ag-badge { font-size: 9px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; }
.lo-badge { color: var(--teal); background: rgba(34,211,238,0.1); border: 1px solid rgba(34,211,238,0.2); }
.re-badge { color: var(--a); background: rgba(198,156,57,0.1); border: 1px solid rgba(198,156,57,0.2); }
.ag-avatar-wrap { position: relative; }
.ag-avatar { width: 52px; height: 52px; border-radius: 12px; object-fit: cover; display: block; }
.ag-avatar-ring { position: absolute; inset: -3px; border-radius: 14px; border: 2px solid; opacity: 0.5; }
.lo-ring { border-color: var(--teal); }
.re-ring { border-color: var(--a); }
.ag-name { font-size: 18px; font-weight: 600; color: #f0f4ff; margin-bottom: 2px; }
.ag-role { font-size: 11px; color: var(--muted); }
.ag-nmls { font-size: 10px; color: rgba(120,136,176,0.5); margin-top: 2px; }
.ag-sep { height: 1px; margin: 14px 0; }
.lo-sep { background: linear-gradient(90deg, rgba(34,211,238,0.3), transparent); }
.re-sep { background: linear-gradient(90deg, rgba(198,156,57,0.3), transparent); }
.ag-contacts { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
.ag-link { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.lo-alink:hover { color: var(--teal); }
.re-alink:hover { color: var(--a); }
.ag-cta { display: block; text-align: center; text-decoration: none; font-size: 12px; font-weight: 600; padding: 11px; border-radius: 10px; background: linear-gradient(135deg, var(--blue), var(--violet)); color: #fff; box-shadow: 0 4px 16px rgba(74,143,255,0.3); transition: box-shadow 0.2s; }
.ag-cta:hover { box-shadow: 0 8px 24px rgba(74,143,255,0.5); }

@media (max-width: 820px) {
  .glass-nav { padding: 14px 24px; }
  .gn-pair { display: none; }
  .hero-section { padding: 40px 24px 24px; }
  .hero-glass-panel { padding: 32px 24px; }
  .cards-section { padding: 16px 24px 48px; }
}
</style>
